import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/hooks/useAnalytics";

interface HeaderProps {
  showActions?: boolean;
}

const Header = ({ showActions = true }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackNavigation } = useAnalytics();
  const isHomepage =
    location.pathname === "/" || location.pathname === "/homepage";
  const showDashboard = import.meta.env.VITE_SHOW_DASHBOARD === "true";

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    if (isHomepage) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/", { replace: true });
      // Use setTimeout to ensure navigation completes before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
    setMobileMenuOpen(false);
  };

  // Track which section is currently in view
  useEffect(() => {
    if (!isHomepage) return;

    const observerOptions = {
      root: null,
      rootMargin: "-10% 0px -60% 0px",
      threshold: 0.1,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Find the entry with the highest intersection ratio
      const intersectingEntries = entries.filter(
        (entry) => entry.isIntersecting
      );
      if (intersectingEntries.length > 0) {
        const mostVisible = intersectingEntries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        setActiveSection(mostVisible.target.id);
      }
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    const sections = ["home", "how-it-works", "our-values"];
    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [isHomepage]);

  // Handle header hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 100) {
        // Always show header at top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else {
        // Scrolling up - show header
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 bg-[#F7F5F2] transition-transform duration-300 ease-in-out ${
        isVisible ? "top-0 translate-y-0" : "-top-16 -translate-y-full"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between`}>
          {/* Logo/Brand - Centered when showActions is false, left-aligned otherwise */}
          <Link
            to="/"
            className="flex items-center"
            onClick={() => trackNavigation("home", "header_logo")}
          >
              <img
                src="/images/logo3.png"
                alt="Wabisabi Logo"
                className="w-[100px] h-[100px]"
              />
          </Link>

          {/* Desktop Navigation - Hidden when showActions is false */}
          {showActions && (
            <nav className="hidden md:flex items-center space-x-4">
              <button
                onClick={() => scrollToSection("home")}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                }}
              >
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    isHomepage && activeSection === "home"
                      ? "bg-[#60416D] border-[#60416D]"
                      : "border-[#2C2C2C]"
                  }`}
                >
                  {isHomepage && activeSection === "home" && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                Home
              </button>
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                }}
              >
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    isHomepage && activeSection === "how-it-works"
                      ? "bg-[#60416D] border-[#60416D]"
                      : "border-[#2C2C2C]"
                  }`}
                >
                  {isHomepage && activeSection === "how-it-works" && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                How it Works
              </button>
              <button
                onClick={() => scrollToSection("our-values")}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                }}
              >
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    isHomepage && activeSection === "our-values"
                      ? "bg-[#60416D] border-[#60416D]"
                      : "border-[#2C2C2C]"
                  }`}
                >
                  {isHomepage && activeSection === "our-values" && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                Our Values
              </button>
              <Link
                to="/about"
                className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
                style={{
                  fontWeight: 700,
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  verticalAlign: "middle",
                }}
                onClick={() => trackNavigation("about", "header")}
              >
                <div
                  className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                    location.pathname === "/about"
                      ? "bg-[#60416D] border-[#60416D]"
                      : "border-[#2C2C2C]"
                  }`}
                >
                  {location.pathname === "/about" && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                About
              </Link>
              {!isHomepage && showActions && showDashboard && (
                <Link
                  to="/"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === "/" ||
                    location.pathname === "/homepage"
                      ? "bg-primary/10 text-primary"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Homepage
                </Link>
              )}
              <Link
                to="/login"
                onClick={() => trackNavigation("login", "header")}
              >
                <Button className="bg-wabisabi-plum hover:bg-wabisabi-plum-dark text-white">
                  Sign In
                </Button>
              </Link>
            </nav>
          )}

          {/* Mobile menu button - Positioned absolutely when logo is centered */}
          <div className={`md:hidden`}>
            {showActions && (
              <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && showActions && (
        <div className="md:hidden bg-white border-t">
          <div className="container mx-auto px-4 py-2 space-y-1">
            <button
              onClick={() => scrollToSection("home")}
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              style={{
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
              }}
            >
              <div
                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                  isHomepage && activeSection === "home"
                    ? "bg-[#60416D] border-[#60416D]"
                    : "border-[#2C2C2C]"
                }`}
              >
                {isHomepage && activeSection === "home" && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              Home
            </button>
            <button
              onClick={() => scrollToSection("how-it-works")}
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              style={{
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
              }}
            >
              <div
                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                  isHomepage && activeSection === "how-it-works"
                    ? "bg-[#60416D] border-[#60416D]"
                    : "border-[#2C2C2C]"
                }`}
              >
                {isHomepage && activeSection === "how-it-works" && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              How it Works
            </button>
            <button
              onClick={() => scrollToSection("our-values")}
              className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              style={{
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
              }}
            >
              <div
                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                  isHomepage && activeSection === "our-values"
                    ? "bg-[#60416D] border-[#60416D]"
                    : "border-[#2C2C2C]"
                }`}
              >
                {isHomepage && activeSection === "our-values" && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              Our Values
            </button>
            <Link
              to="/about"
              className="flex items-center gap-2 px-3 py-2 rounded-md text-gray-700 hover:bg-gray-100"
              style={{
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
              }}
              onClick={() => {
                trackNavigation("about", "header");
                setMobileMenuOpen(false);
              }}
            >
              <div
                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                  location.pathname === "/about"
                    ? "bg-[#60416D] border-[#60416D]"
                    : "border-[#2C2C2C]"
                }`}
              >
                {location.pathname === "/about" && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              About
            </Link>
            <Link
              to="/login"
              className="w-full"
              onClick={() => {
                trackNavigation("login", "header");
                setMobileMenuOpen(false);
              }}
            >
              <Button className="w-full bg-wabisabi-plum hover:bg-wabisabi-plum-dark text-white">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
